import { Controller } from "@hotwired/stimulus";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";

export default class extends Controller {
  static targets = ["button", "menu", "template"];
  static classes = ["flip"];
  static values = {
    autoClose: { type: Boolean, default: true }, // Whether to close the menu when clicking on an item
    nested: { type: Boolean, default: false }, // Whether the menu is nested
    hover: { type: Boolean, default: false }, // Whether to show the menu on hover
    autoPosition: { type: Boolean, default: true }, // Whether to automatically position the menu
    lazyLoad: { type: Boolean, default: false }, // Whether to lazy load the menu content
    placement: { type: String, default: "bottom-start" }, // The placement(s) of the menu - can be multiple separated by spaces
    dialogMode: { type: Boolean, default: true }, // Whether to use dialog mode
    turboFrameSrc: { type: String, default: "" }, // URL for Turbo Frame lazy loading
    lockScroll: { type: Boolean, default: false }, // Whether to lock scroll when menu is open
  };

  connect() {
    // Initialize scroll lock state
    this.isScrollLocked = false;

    // Initialize non-dialog menu if dialogMode is false
    if (!this.dialogModeValue && !this.menuTarget.hasAttribute("role")) {
      this.menuTarget.setAttribute("role", "menu");
      this.menuTarget.setAttribute("aria-modal", "false");
      this.menuTarget.setAttribute("tabindex", "-1");
    }

    this.menuTarget.addEventListener("menu-item-clicked", () => {
      if (this.autoCloseValue) {
        this.close();
        let parent = this.element.closest('[data-controller="dropdown-popover"]');
        while (parent) {
          const parentController = this.application.getControllerForElementAndIdentifier(parent, "dropdown-popover");
          if (parentController && parentController.autoCloseValue) {
            parentController.close();
          }
          parent = parent.parentElement.closest('[data-controller="dropdown-popover"]');
        }
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    this.buttonTarget.addEventListener("keydown", (event) => {
      if (!this.isOpen) return;

      const menuController = this.application.getControllerForElementAndIdentifier(this.menuTarget, "menu");

      if (event.key === "ArrowDown") {
        event.preventDefault();
        menuController.selectFirst();
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        menuController.selectLast();
      }
    });

    // Add scroll listener to update position
    this.scrollHandler = () => {
      if (this.isOpen && this.autoPositionValue) {
        this.#updatePosition();
      }
    };
    window.addEventListener("scroll", this.scrollHandler, true);

    // Update position when window is resized or menu content changes
    this.resizeObserver = new ResizeObserver(() => {
      if (this.isOpen) {
        this.#updatePosition();
      }
    });

    // Observe both document.body and the menuTarget element
    this.resizeObserver.observe(document.body);
    this.resizeObserver.observe(this.menuTarget);

    // Add MutationObserver to detect content changes inside the menu
    this.mutationObserver = new MutationObserver(() => {
      if (this.isOpen) {
        // Slight delay to allow DOM changes to complete
        setTimeout(() => this.#updatePosition(), 10);
      }
    });

    this.mutationObserver.observe(this.menuTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    // Add hover functionality for hover-enabled dropdowns
    if (this.hoverValue) {
      let hoverTimeout;
      let openTimeout;

      // Track if mouse is over the dropdown area
      let isMouseOverDropdown = false;

      this.buttonTarget.addEventListener("mouseenter", () => {
        isMouseOverDropdown = true;
        clearTimeout(hoverTimeout);
        clearTimeout(openTimeout);

        // For nested dropdowns, add a small delay before opening to prevent
        // multiple submenus from opening when quickly moving between triggers
        if (this.nestedValue) {
          openTimeout = setTimeout(() => {
            this.show();
          }, 80); // 80ms delay for nested submenus
        } else {
          this.show();
        }
      });

      this.buttonTarget.addEventListener("mouseleave", () => {
        isMouseOverDropdown = false;
        clearTimeout(openTimeout); // Cancel pending open if mouse leaves
        // Check if mouse is not over the menu
        hoverTimeout = setTimeout(() => {
          if (!isMouseOverDropdown && this.isOpen) {
            this.close();
          }
        }, 50); // Very short delay to allow moving to menu
      });

      this.menuTarget.addEventListener("mouseenter", () => {
        isMouseOverDropdown = true;
        clearTimeout(hoverTimeout);
      });

      this.menuTarget.addEventListener("mouseleave", () => {
        isMouseOverDropdown = false;
        hoverTimeout = setTimeout(() => {
          if (!isMouseOverDropdown && this.isOpen) {
            this.close();
          }
        }, 50); // Very short delay for smooth experience
      });

      // Keep the existing element mouseleave for nested menu handling
      this.element.addEventListener("mouseleave", (event) => {
        const toElement = event.relatedTarget;
        const isMovingToNestedMenu =
          toElement &&
          (toElement.closest('[data-dropdown-popover-target="menu"]') ||
            toElement.closest('[data-dropdown-popover-target="button"]') ||
            // Check if moving to a child menu
            toElement.closest('[data-controller="dropdown-popover"][data-dropdown-popover-nested-value="true"]'));

        if (!isMovingToNestedMenu) {
          hoverTimeout = setTimeout(() => {
            // Check if any child menu is being hovered before closing
            const hasHoveredChild = Array.from(
              this.menuTarget.querySelectorAll('[data-controller="dropdown-popover"]'),
            ).some(
              (dropdown) =>
                dropdown.matches(":hover") ||
                dropdown.querySelector("[data-dropdown-popover-target='menu']").matches(":hover"),
            );

            if (!hasHoveredChild) {
              this.close();

              // Reset focus state when menu is closed with hover
              if (this.nestedValue) {
                this.buttonTarget.classList.remove("active-menu-path");
                this.buttonTarget.removeAttribute("data-state");
                // Only reset the parent menu if we're not still inside it
                // (e.g., don't reset when moving from nested dropdown to another item in the same menu)
                const parentMenu = this.element.closest('[data-controller="menu"]');
                if (parentMenu && toElement && !parentMenu.contains(toElement)) {
                  const menuController = this.application.getControllerForElementAndIdentifier(parentMenu, "menu");
                  if (menuController) {
                    menuController.reset();
                  }
                }
              }
            }
          }, 100); // Reduced from 200ms to 100ms for more responsive closing
        }
      });
    } else {
      // For non-hover dropdowns, close any open hover dropdowns when hovering the button
      this.buttonTarget.addEventListener("mouseenter", () => {
        const allHoverDropdowns = this.application.controllers.filter(
          (c) => c.identifier === "dropdown-popover" && c !== this && c.isOpen && c.hoverValue,
        );

        allHoverDropdowns.forEach((controller) => {
          controller.close();
        });
      });
    }

    this.#setupTouchPrelock();
  }

  disconnect() {
    // Make sure to unlock scroll when controller disconnects
    if (this.isScrollLocked) {
      this.unlockScroll();
    }
    this.resizeObserver.disconnect();
    // Disconnect mutation observer
    this.mutationObserver.disconnect();
    // Remove scroll listener
    window.removeEventListener("scroll", this.scrollHandler, true);

    if (this.buttonTouchStartHandler) {
      this.buttonTarget.removeEventListener("touchstart", this.buttonTouchStartHandler);
      this.buttonTouchStartHandler = null;
    }

    if (this.buttonTouchEndHandler) {
      this.buttonTarget.removeEventListener("touchend", this.buttonTouchEndHandler);
      this.buttonTarget.removeEventListener("touchcancel", this.buttonTouchEndHandler);
      this.buttonTouchEndHandler = null;
    }

    this.prelockedFromTouch = false;
  }

  get isOpen() {
    if (this.dialogModeValue) {
      return this.menuTarget.open;
    } else {
      return this.menuTarget.classList.contains("hidden") === false;
    }
  }

  async show() {
    // Close all other open dropdowns that aren't in the same hierarchy
    const allDropdowns = this.application.controllers.filter(
      (c) => c.identifier === "dropdown-popover" && c !== this && c.isOpen,
    );

    allDropdowns.forEach((controller) => {
      if (!this.element.contains(controller.element) && !controller.element.contains(this.element)) {
        controller.close();
      }
    });

    // Close any sibling nested dropdowns first (immediately, no animation)
    if (this.nestedValue) {
      const parentMenu = this.element.closest('[data-controller="menu"]');
      if (parentMenu) {
        const siblingDropdowns = parentMenu.querySelectorAll(
          '[data-controller="dropdown-popover"][data-dropdown-popover-nested-value="true"]',
        );
        siblingDropdowns.forEach((dropdown) => {
          const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
          if (controller && controller !== this && controller.isOpen) {
            controller.closeImmediate(); // Close immediately to prevent both being visible
          }
        });
      }
    }

    // If we have lazy loading enabled, load the content now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();
      this.contentLoaded = true;
    }

    // Only top-level dropdowns should lock scroll.
    // Locking on nested submenus can constrain/clamp submenu positioning.
    if (this.lockScrollValue && !this.nestedValue) {
      this.lockScroll();
      this.prelockedFromTouch = false;
    }

    let previousVisibility = "";
    if (this.dialogModeValue) {
      // Keep the menu hidden at a safe position while measuring to avoid Safari scroll jumps.
      previousVisibility = this.menuTarget.style.visibility;
      this.menuTarget.style.visibility = "hidden";
      this.menuTarget.style.left = "0px";
      this.menuTarget.style.top = "0px";
      this.menuTarget.show();
    } else {
      this.menuTarget.classList.remove("hidden");
    }

    this.#updateExpanded();
    try {
      await this.#updatePosition();
    } catch (_error) {
      // Fallback near the trigger if Floating UI fails.
      const rect = this.buttonTarget.getBoundingClientRect();
      this.menuTarget.style.left = `${rect.left}px`;
      this.menuTarget.style.top = `${rect.bottom + 4}px`;
    }

    // Set state attribute for all dropdowns
    this.buttonTarget.setAttribute("data-state", "open");

    // Add active-menu-path class to the button when showing the dropdown
    if (this.nestedValue) {
      this.buttonTarget.classList.add("active-menu-path");
    }

    requestAnimationFrame(() => {
      if (this.dialogModeValue) {
        this.menuTarget.style.visibility = previousVisibility;
      }

      // Add appropriate classes based on placement
      if (this.placementValue.startsWith("top")) {
        this.menuTarget.classList.add("[&[open]]:scale-100", "[&[open]]:opacity-100", "scale-100", "opacity-100");
      } else {
        this.menuTarget.classList.add("[&[open]]:scale-100", "[&[open]]:opacity-100", "scale-100", "opacity-100");
      }

      // Check for autofocus elements inside the menu
      const autofocusElement = this.menuTarget.querySelector('[autofocus="true"], [autofocus]');
      if (autofocusElement) {
        // Focus the autofocus element
        setTimeout(() => {
          this.#focusWithoutScroll(autofocusElement);

          // If it's an input or textarea, position cursor at the end
          if (autofocusElement.tagName === "INPUT" || autofocusElement.tagName === "TEXTAREA") {
            const length = autofocusElement.value.length;
            autofocusElement.setSelectionRange(length, length);
          }
        }, 0);
      } else if (this.nestedValue) {
        this.#focusWithoutScroll(this.menuTarget);
      } else {
        this.#focusWithoutScroll(this.buttonTarget);
      }
    });
  }

  close() {
    this.#closeInternal(false);
  }

  // Immediate close without animation - used when closing sibling menus
  closeImmediate() {
    this.#closeInternal(true);
  }

  #closeInternal(immediate = false) {
    // Unlock scroll if it was locked
    if (this.isScrollLocked) {
      this.unlockScroll();
    }

    // Reset focus states in the menu before closing
    const menuController = this.application.getControllerForElementAndIdentifier(this.menuTarget, "menu");
    if (menuController) {
      menuController.reset();
    }

    // Close any child dropdowns first
    const childDropdowns = this.menuTarget.querySelectorAll('[data-controller="dropdown-popover"]');
    childDropdowns.forEach((dropdown) => {
      const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
      if (controller && controller.isOpen) {
        immediate ? controller.closeImmediate() : controller.close();
      }
    });

    // Remove all active-menu-path classes within this menu
    this.menuTarget.querySelectorAll(".active-menu-path").forEach((el) => {
      el.classList.remove("active-menu-path");
    });
    // Also remove from the button that triggered this dropdown and remove state attribute
    this.buttonTarget.classList.remove("active-menu-path");
    this.buttonTarget.removeAttribute("data-state");

    // If this is a hover-enabled dropdown, ensure we blur any focused elements
    if (this.hoverValue) {
      const focusedElement = this.element.querySelector(":focus");
      if (focusedElement) {
        focusedElement.blur();
      }
    }

    this.menuTarget.classList.remove("scale-100", "opacity-100", "[&[open]]:scale-100", "[&[open]]:opacity-100");

    if (immediate) {
      // Close immediately without animation delay
      if (this.dialogModeValue) {
        this.menuTarget.close();
      } else {
        this.menuTarget.classList.add("hidden");
      }
      this.#updateExpanded();
    } else {
      // Animated close with delay
      setTimeout(() => {
        if (this.dialogModeValue) {
          this.menuTarget.close();
        } else {
          this.menuTarget.classList.add("hidden");
        }
        this.#updateExpanded();
      }, 100);
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.show();
  }

  closeOnClickOutside({ target }) {
    const isClickInNestedDropdown = target.closest('[data-dropdown-popover-nested-value="true"]');
    if (isClickInNestedDropdown) return;

    if (!this.element.contains(target)) {
      this.close();
    }
  }

  // Prevent close method to stop click propagation
  preventClose(event) {
    // Stop propagation to prevent the closeOnClickOutside handler from being triggered
    event.stopPropagation();
  }

  // Reset method to handle dialog close events
  reset() {
    // This method is called when the dialog is closed
    // It's responsible for any cleanup needed after closing

    // If we have a menu controller, reset its state
    const menuController = this.application.getControllerForElementAndIdentifier(this.menuTarget, "menu");
    if (menuController && typeof menuController.reset === "function") {
      menuController.reset();
    }

    // Dispatch a custom event that can be listened for by other controllers
    const resetEvent = new CustomEvent("dropdown-reset", {
      bubbles: true,
      detail: { controller: this },
    });
    this.element.dispatchEvent(resetEvent);
  }

  #updateExpanded() {
    this.buttonTarget.ariaExpanded = this.isOpen;
  }

  async #loadTemplateContent() {
    // Find the container in the menu to append content to
    const container = this.menuTarget.querySelector("[data-dropdown-popover-content]") || this.menuTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "dropdown-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          this.#refreshMenuController();
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          this.#refreshMenuController();
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading (existing behavior)
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);

      this.#refreshMenuController();
    }
  }

  #refreshMenuController() {
    // Refresh the menu controller to pick up new targets from the loaded content
    setTimeout(() => {
      const menuController = this.application.getControllerForElementAndIdentifier(this.menuTarget, "menu");
      if (menuController) {
        // Disconnect and reconnect the menu controller to refresh its targets
        menuController.disconnect();
        menuController.connect();
      }
    }, 10);
  }

  async #updatePosition() {
    // Parse placement value to support multiple placements
    const placements = this.placementValue.split(/[\s,]+/).filter(Boolean);
    const primaryPlacement = placements[0] || "bottom-start";
    const fallbackPlacements = placements.slice(1);

    const middleware = [
      offset(this.nestedValue ? -4 : 4), // 0 offset for nested, 4px for regular
      flip({
        fallbackPlacements: fallbackPlacements.length > 0 ? fallbackPlacements : ["top-start", "bottom-start"],
      }),
      shift({ padding: 8 }), // Keep 8px padding from window edges
    ];

    if (this.nestedValue) {
      // For nested dropdowns, position to the right of the button
      const { x, y } = await computePosition(this.buttonTarget, this.menuTarget, {
        placement: "right-start",
        middleware,
      });

      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    } else {
      // Use the primary placement from the controller
      const { x, y } = await computePosition(this.buttonTarget, this.menuTarget, {
        placement: primaryPlacement,
        middleware: this.autoPositionValue ? middleware : [offset(this.nestedValue ? -4 : 4)],
      });

      Object.assign(this.menuTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    }
  }

  #focusWithoutScroll(element) {
    if (!element || typeof element.focus !== "function") return;

    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  }

  #setupTouchPrelock() {
    if (!this.lockScrollValue || this.hoverValue || this.nestedValue || !this.hasButtonTarget) return;

    this.buttonTouchStartHandler = (event) => {
      if (this.isOpen || this.isScrollLocked) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("a, input, textarea, select")) return;

      this.lockScroll();
      this.prelockedFromTouch = true;
    };

    this.buttonTouchEndHandler = () => {
      if (!this.prelockedFromTouch) return;

      // If touch interaction did not open the dropdown, revert the pre-lock.
      setTimeout(() => {
        if (!this.prelockedFromTouch || this.isOpen) return;
        if (this.isScrollLocked) {
          this.unlockScroll();
        }
        this.prelockedFromTouch = false;
      }, 0);
    };

    this.buttonTarget.addEventListener("touchstart", this.buttonTouchStartHandler, { passive: true });
    this.buttonTarget.addEventListener("touchend", this.buttonTouchEndHandler);
    this.buttonTarget.addEventListener("touchcancel", this.buttonTouchEndHandler);
  }

  // Add scroll locking methods
  lockScroll() {
    if (this.isScrollLocked) return;

    this.scrollLockTarget = this.#getScrollLockTarget();

    if (this.scrollLockTarget !== document.body) {
      this.previousTargetOverflow = this.scrollLockTarget.style.overflow;
      this.previousTargetPaddingRight = this.scrollLockTarget.style.paddingRight;
      this.previousTargetOverscrollBehavior = this.scrollLockTarget.style.overscrollBehavior;

      const targetStyles = window.getComputedStyle(this.scrollLockTarget);
      const borderLeft = parseFloat(targetStyles.borderLeftWidth) || 0;
      const borderRight = parseFloat(targetStyles.borderRightWidth) || 0;
      const rawDelta = this.scrollLockTarget.offsetWidth - this.scrollLockTarget.clientWidth;
      const scrollbarWidth = Math.max(0, rawDelta - borderLeft - borderRight);
      if (scrollbarWidth > 0) {
        const computedPaddingRight = parseFloat(targetStyles.paddingRight) || 0;
        this.scrollLockTarget.style.paddingRight = `${computedPaddingRight + scrollbarWidth}px`;
      }

      this.scrollLockTarget.style.overflow = "hidden";
      this.scrollLockTarget.style.overscrollBehavior = "contain";
      this.isScrollLocked = true;
      return;
    }

    this.#lockBodyScrollWithCompensation();

    this.isScrollLocked = true;
  }

  unlockScroll() {
    if (!this.isScrollLocked) return;

    if (this.scrollLockTarget && this.scrollLockTarget !== document.body) {
      this.scrollLockTarget.style.overflow = this.previousTargetOverflow || "";
      this.scrollLockTarget.style.paddingRight = this.previousTargetPaddingRight || "";
      this.scrollLockTarget.style.overscrollBehavior = this.previousTargetOverscrollBehavior || "";
      this.scrollLockTarget = null;
      this.isScrollLocked = false;
      return;
    }

    this.#unlockBodyScrollWithCompensation();

    this.isScrollLocked = false;
    this.scrollLockTarget = null;
  }

  #getScrollLockTarget() {
    const parentOverlay = this.element.closest(
      'dialog[open][data-modal-target="dialog"], dialog[open][data-slideover-target="dialog"], dialog[open][data-drawer-target="dialog"], dialog[open], .modal-div.modal-open',
    );

    if (parentOverlay instanceof HTMLElement) {
      const nearestScrollableWithinOverlay = this.#findNearestScrollableAncestor(this.buttonTarget, parentOverlay);
      return nearestScrollableWithinOverlay || parentOverlay;
    }

    return document.body;
  }

  #findNearestScrollableAncestor(fromElement, boundaryElement) {
    let current = fromElement?.parentElement;

    while (current && current !== boundaryElement) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === "auto" || overflowY === "scroll") && current.scrollHeight > current.clientHeight;

      if (isScrollable) return current;
      current = current.parentElement;
    }

    if (boundaryElement instanceof HTMLElement) {
      const style = window.getComputedStyle(boundaryElement);
      const overflowY = style.overflowY;
      const isScrollable =
        (overflowY === "auto" || overflowY === "scroll") && boundaryElement.scrollHeight > boundaryElement.clientHeight;
      if (isScrollable) return boundaryElement;
    }

    return null;
  }

  #lockBodyScrollWithCompensation() {
    if (this.hasRegisteredBodyLock) return;

    const state = this.#getBodyLockState();
    if (state.count === 0) {
      const scrollbarWidth = this.#measureScrollbarWidth();
      state.previousBodyOverflow = document.body.style.overflow;
      state.previousBodyPaddingRight = document.body.style.paddingRight;

      if (scrollbarWidth > 0) {
        const bodyStyles = window.getComputedStyle(document.body);
        const computedBodyPaddingRight = parseFloat(bodyStyles.paddingRight) || 0;
        document.body.style.paddingRight = `${computedBodyPaddingRight + scrollbarWidth}px`;
      }

      document.body.style.overflow = "hidden";

      state.compensatedFixedElements = [];
      if (scrollbarWidth > 0) {
        document.querySelectorAll(".fixed").forEach((element) => {
          if (!(element instanceof HTMLElement) || this.#shouldSkipFixedCompensation(element)) return;

          state.compensatedFixedElements.push({
            element,
            previousPaddingRight: element.style.paddingRight,
          });

          const fixedStyles = window.getComputedStyle(element);
          const computedFixedPaddingRight = parseFloat(fixedStyles.paddingRight) || 0;
          element.style.paddingRight = `${computedFixedPaddingRight + scrollbarWidth}px`;
        });
      }
    }

    state.count += 1;
    this.hasRegisteredBodyLock = true;
  }

  #unlockBodyScrollWithCompensation() {
    if (!this.hasRegisteredBodyLock) return;

    const state = this.#getBodyLockState();
    state.count = Math.max(0, state.count - 1);

    if (state.count === 0) {
      document.body.style.overflow = state.previousBodyOverflow || "";
      document.body.style.paddingRight = state.previousBodyPaddingRight || "";
      state.previousBodyOverflow = "";
      state.previousBodyPaddingRight = "";

      state.compensatedFixedElements.forEach(({ element, previousPaddingRight }) => {
        if (!(element instanceof HTMLElement)) return;
        element.style.paddingRight = previousPaddingRight || "";
      });
      state.compensatedFixedElements = [];
    }

    this.hasRegisteredBodyLock = false;
  }

  #measureScrollbarWidth() {
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar";
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = Math.max(0, outer.offsetWidth - inner.offsetWidth);
    outer.parentNode?.removeChild(outer);

    return scrollbarWidth;
  }

  #getBodyLockState() {
    if (!window.__floatingBodyScrollLockState) {
      window.__floatingBodyScrollLockState = {
        count: 0,
        previousBodyOverflow: "",
        previousBodyPaddingRight: "",
        compensatedFixedElements: [],
      };
    }

    return window.__floatingBodyScrollLockState;
  }

  #shouldSkipFixedCompensation(element) {
    return Boolean(
      element.closest(
        'dialog[data-context-menu-target="menu"], [data-slideover-target="dialog"], [data-drawer-target="dialog"]',
      ),
    );
  }
}
